import React, { useState, useEffect } from 'react';
import { coachDashboardAPI } from '../services/api';
import { MarkdownContent } from '../utils/formatMarkdown';
import styles from '../styles/UserDetailView.module.css';

/**
 * UserDetailView - Detailed view of a user's journey for coaches
 *
 * Shows:
 * - User journey timeline and stats
 * - Coach summaries
 * - Weekly observations
 * - Session history and notes
 *
 * Props:
 * - user: User object from the users list
 * - onBack: Callback to return to users list
 */
function UserDetailView({ user, onBack }) {
  const [stats, setStats] = useState(null);
  const [summaries, setSummaries] = useState([]); // Contains both automated summaries AND manual notes
  const [observations, setObservations] = useState([]);
  const [history, setHistory] = useState(null);
  const [todos, setTodos] = useState([]);
  const [objective, setObjective] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('journey'); // journey, summaries, sessions, actionPlan
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editingTodoText, setEditingTodoText] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingSummaryId, setEditingSummaryId] = useState(null);
  const [editingSummaryText, setEditingSummaryText] = useState('');
  const [addingTodoToTier, setAddingTodoToTier] = useState(null); // Track which tier is getting a new todo
  const [newTodoText, setNewTodoText] = useState('');
  const [newTodoIsOptional, setNewTodoIsOptional] = useState(false);

  useEffect(() => {
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const loadUserData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [statsRes, summariesRes, observationsRes, historyRes, todosRes] = await Promise.all([
        coachDashboardAPI.getUserStats(user.id),
        coachDashboardAPI.getUserSummaries(user.id), // Now includes both automated summaries AND manual notes
        coachDashboardAPI.getUserObservations(user.id),
        coachDashboardAPI.getUserHistory(user.id),
        coachDashboardAPI.getUserTodos(user.id)
      ]);

      setStats(statsRes.stats);
      setSummaries(summariesRes.summaries || []);
      setObservations(observationsRes.observations || []);
      setHistory(historyRes);
      setTodos(todosRes.todos || []);
      setObjective(todosRes.objective);
    } catch (err) {
      console.error('[UserDetailView] Error loading user data:', err);
      setError(err.message || 'Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTodo = async (todoId, isCompleted) => {
    try {
      await coachDashboardAPI.updateUserTodo(user.id, todoId, { is_completed: !isCompleted });
      // Update local state
      setTodos(todos.map(todo =>
        todo.id === todoId ? { ...todo, is_completed: !isCompleted } : todo
      ));
    } catch (err) {
      console.error('[UserDetailView] Error toggling todo:', err);
      alert('Failed to update todo');
    }
  };

  const handleEditTodo = (todo) => {
    setEditingTodoId(todo.id);
    setEditingTodoText(todo.todo_text);
  };

  const handleSaveTodo = async (todoId) => {
    try {
      await coachDashboardAPI.updateUserTodo(user.id, todoId, { todo_text: editingTodoText });
      // Update local state
      setTodos(todos.map(todo =>
        todo.id === todoId ? { ...todo, todo_text: editingTodoText } : todo
      ));
      setEditingTodoId(null);
      setEditingTodoText('');
    } catch (err) {
      console.error('[UserDetailView] Error saving todo:', err);
      alert('Failed to save todo');
    }
  };

  const handleDeleteTodo = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;

    try {
      await coachDashboardAPI.deleteUserTodo(user.id, todoId);
      // Update local state
      setTodos(todos.filter(todo => todo.id !== todoId));
    } catch (err) {
      console.error('[UserDetailView] Error deleting todo:', err);
      alert('Failed to delete todo');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNoteText.trim()) return;

    try {
      const response = await coachDashboardAPI.createUserNote(user.id, newNoteText.trim());
      // Add the new note to the summaries list (it's also a coach-summary entry)
      setSummaries([response.note, ...summaries]);
      // Reset form
      setNewNoteText('');
      setIsAddingNote(false);
    } catch (err) {
      console.error('[UserDetailView] Error creating note:', err);
      alert('Failed to create note: ' + err.message);
    }
  };

  const handleEditSummary = (summary) => {
    setEditingSummaryId(summary.id);
    setEditingSummaryText(summary.summary_text);
  };

  const handleSaveSummary = async (summaryId) => {
    if (!editingSummaryText.trim()) return;

    try {
      // Find the summary to get its documentId
      const summary = summaries.find(s => s.id === summaryId);
      if (!summary) return;

      const response = await coachDashboardAPI.updateUserNote(user.id, summary.documentId, editingSummaryText.trim());
      // Update local state
      setSummaries(summaries.map(s =>
        s.id === summaryId ? { ...s, summary_text: editingSummaryText.trim(), updatedAt: response.note.updated_at } : s
      ));
      setEditingSummaryId(null);
      setEditingSummaryText('');
    } catch (err) {
      console.error('[UserDetailView] Error updating note:', err);
      alert('Failed to update note: ' + err.message);
    }
  };

  const handleDeleteSummary = async (summaryId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      // Find the summary to get its documentId
      const summary = summaries.find(s => s.id === summaryId);
      if (!summary) return;

      await coachDashboardAPI.deleteUserNote(user.id, summary.documentId);
      // Update local state
      setSummaries(summaries.filter(s => s.id !== summaryId));
    } catch (err) {
      console.error('[UserDetailView] Error deleting note:', err);
      alert('Failed to delete note: ' + err.message);
    }
  };

  const handleAddTodo = async (e, tierNumber, tierTitle) => {
    e.preventDefault();
    if (!newTodoText.trim()) return;

    try {
      // Find the highest order_index in this tier
      const tierTodos = todos.filter(t => t.tier_number === tierNumber && !t.is_deleted);
      const maxOrderIndex = tierTodos.length > 0
        ? Math.max(...tierTodos.map(t => t.order_index))
        : -1;

      const response = await coachDashboardAPI.createUserTodo(user.id, {
        tier_number: tierNumber,
        tier_title: tierTitle,
        todo_text: newTodoText.trim(),
        is_optional: newTodoIsOptional,
        order_index: maxOrderIndex + 1
      });

      // Add the new todo to local state
      setTodos([...todos, response.todo]);

      // Reset form
      setNewTodoText('');
      setNewTodoIsOptional(false);
      setAddingTodoToTier(null);
    } catch (err) {
      console.error('[UserDetailView] Error creating todo:', err);
      alert('Failed to create todo: ' + err.message);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={onBack} className={styles.backButton}>← Back to Users</button>
        </div>
        <div className={styles.loading}>Loading user data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={onBack} className={styles.backButton}>← Back to Users</button>
        </div>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  // Calculate weeks in journey
  const weeksInJourney = stats?.journey_start_date
    ? Math.floor((new Date() - new Date(stats.journey_start_date)) / (7 * 24 * 60 * 60 * 1000)) + 1
    : 0;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButton}>← Back to Users</button>
        <h2 className={styles.userName}>{user.name || user.email}</h2>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'journey' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('journey')}
        >
          Your Journey
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'actionPlan' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('actionPlan')}
        >
          Action Plan ({todos.filter(t => !t.is_deleted && !t.is_completed).length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'summaries' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('summaries')}
        >
          Coach Summaries ({summaries.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'sessions' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Sessions ({history?.sessions?.length || 0})
        </button>
      </div>

      {/* Journey Tab */}
      {activeTab === 'journey' && (
        <div className={styles.content}>
          {/* Journey Stats Grid */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Journey Start</div>
              <div className={styles.statValue}>
                {stats?.journey_start_date
                  ? new Date(stats.journey_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : 'Not started'}
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Week in Journey</div>
              <div className={styles.statValue}>{weeksInJourney}</div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Days Active</div>
              <div className={styles.statValue}>{stats?.days_active || 0}</div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Engagement Trend</div>
              <div className={`${styles.statValue} ${styles[`trend-${stats?.engagement_trend}`]}`}>
                {stats?.engagement_trend || 'stable'}
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Todos Completed</div>
              <div className={styles.statValue}>{stats?.todos_completed || 0}</div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Total Sessions</div>
              <div className={styles.statValue}>{stats?.total_sessions || 0}</div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Articles Read</div>
              <div className={styles.statValue}>{stats?.articles_read || 0}</div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statLabel}>Videos Watched</div>
              <div className={styles.statValue}>{stats?.videos_watched || 0}</div>
            </div>
          </div>

          {/* Weekly Observations */}
          {observations.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Weekly Observations</h3>
              <div className={styles.observationsList}>
                {observations.map((obs) => (
                  <div key={obs.id} className={styles.observation}>
                    <div className={styles.observationHeader}>
                      <span className={styles.observationWeek}>Week {obs.week_number}</span>
                      <span className={styles.observationDate}>
                        {new Date(obs.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className={styles.observationText}>{obs.observation_text}</p>
                    {obs.confidence_score && (
                      <div className={styles.observationConfidence}>
                        Confidence: {(obs.confidence_score * 100).toFixed(0)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {observations.length === 0 && (
            <div className={styles.emptyState}>
              <p>No weekly observations yet. Observations are generated as the user progresses.</p>
            </div>
          )}
        </div>
      )}

      {/* Action Plan Tab */}
      {activeTab === 'actionPlan' && (
        <div className={styles.content}>
          {objective && (
            <div className={styles.objectiveBanner}>
              <h3 className={styles.objectiveTitle}>Current Mission</h3>
              <p className={styles.objectiveText}>{objective}</p>
            </div>
          )}

          {todos.length > 0 ? (
            (() => {
              // Group todos by tier
              const tierMap = {};
              todos.forEach(todo => {
                if (todo.is_deleted) return; // Skip deleted todos
                const tierNum = todo.tier_number;
                if (!tierMap[tierNum]) {
                  tierMap[tierNum] = {
                    tier_number: tierNum,
                    tier_title: todo.tier_title,
                    todos: []
                  };
                }
                tierMap[tierNum].todos.push(todo);
              });

              const sortedTiers = Object.values(tierMap).sort((a, b) => a.tier_number - b.tier_number);

              return (
                <div className={styles.actionPlanContainer}>
                  {sortedTiers.map(tier => (
                    <div key={tier.tier_number} className={styles.tierSection}>
                      <div className={styles.tierHeader}>
                        <h4 className={styles.tierTitle}>{tier.tier_title}</h4>
                        <button
                          className={styles.addTodoButton}
                          onClick={() => setAddingTodoToTier(tier.tier_number)}
                          title="Add todo to this stage"
                        >
                          +
                        </button>
                      </div>
                      <div className={styles.todosList}>
                        {tier.todos.sort((a, b) => a.order_index - b.order_index).map(todo => (
                          <div key={todo.id} className={`${styles.todoItem} ${todo.is_completed ? styles.todoCompleted : ''}`}>
                            {editingTodoId === todo.id ? (
                              <div className={styles.todoEditing}>
                                <input
                                  type="text"
                                  value={editingTodoText}
                                  onChange={(e) => setEditingTodoText(e.target.value)}
                                  className={styles.todoInput}
                                />
                                <div className={styles.todoEditActions}>
                                  <button onClick={() => handleSaveTodo(todo.id)} className={styles.saveButton}>Save</button>
                                  <button onClick={() => setEditingTodoId(null)} className={styles.cancelButton}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className={styles.todoLeft}>
                                  <input
                                    type="checkbox"
                                    checked={todo.is_completed}
                                    onChange={() => handleToggleTodo(todo.id, todo.is_completed)}
                                    className={styles.todoCheckbox}
                                  />
                                  <span className={styles.todoText}>
                                    {todo.is_optional && <span className={styles.optionalBadge}>Optional</span>}
                                    {todo.todo_text}
                                  </span>
                                </div>
                                <div className={styles.todoActions}>
                                  <button onClick={() => handleEditTodo(todo)} className={styles.editButton}>Edit</button>
                                  <button onClick={() => handleDeleteTodo(todo.id)} className={styles.deleteButton}>Delete</button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}

                        {/* Add Todo Form */}
                        {addingTodoToTier === tier.tier_number && (
                          <form className={styles.addTodoForm} onSubmit={(e) => handleAddTodo(e, tier.tier_number, tier.tier_title)}>
                            <input
                              type="text"
                              value={newTodoText}
                              onChange={(e) => setNewTodoText(e.target.value)}
                              placeholder="Enter new todo..."
                              className={styles.todoInput}
                              autoFocus
                            />
                            <div className={styles.addTodoOptions}>
                              <label className={styles.optionalCheckbox}>
                                <input
                                  type="checkbox"
                                  checked={newTodoIsOptional}
                                  onChange={(e) => setNewTodoIsOptional(e.target.checked)}
                                />
                                <span>Optional</span>
                              </label>
                              <div className={styles.addTodoActions}>
                                <button type="submit" className={styles.saveButton}>Add</button>
                                <button
                                  type="button"
                                  className={styles.cancelButton}
                                  onClick={() => {
                                    setAddingTodoToTier(null);
                                    setNewTodoText('');
                                    setNewTodoIsOptional(false);
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : (
            <div className={styles.emptyState}>
              <p>No action plan yet. User needs to set a goal in their conversation.</p>
            </div>
          )}
        </div>
      )}

      {/* Summaries Tab */}
      {activeTab === 'summaries' && (
        <div className={styles.content}>
          {/* Add Note Button */}
          <div className={styles.summariesSection}>
            <div className={styles.notesSectionHeader}>
              <h3 className={styles.sectionTitle}>Coach Summaries & Notes</h3>
              {!isAddingNote && (
                <button
                  className={styles.addNoteButton}
                  onClick={() => setIsAddingNote(true)}
                >
                  + Add Note
                </button>
              )}
            </div>

            {/* Add Note Form */}
            {isAddingNote && (
              <form className={styles.noteForm} onSubmit={handleAddNote}>
                <textarea
                  className={styles.noteTextarea}
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="Add your observations, thoughts, or reminders about this user..."
                  rows={4}
                  autoFocus
                />
                <div className={styles.noteFormActions}>
                  <button type="submit" className={styles.saveButton}>Save Note</button>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      setIsAddingNote(false);
                      setNewNoteText('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Combined Summaries & Notes List */}
            {summaries.length > 0 ? (
              <div className={styles.summariesList}>
                {summaries.map((summary) => {
                  const isNote = summary.week_number === -1;

                  return (
                    <div key={summary.id} className={isNote ? styles.coachNote : styles.summary}>
                      {editingSummaryId === summary.id && isNote ? (
                        <div className={styles.noteEditing}>
                          <textarea
                            className={styles.noteTextarea}
                            value={editingSummaryText}
                            onChange={(e) => setEditingSummaryText(e.target.value)}
                            rows={4}
                          />
                          <div className={styles.noteFormActions}>
                            <button onClick={() => handleSaveSummary(summary.id)} className={styles.saveButton}>Save</button>
                            <button onClick={() => setEditingSummaryId(null)} className={styles.cancelButton}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className={isNote ? styles.noteHeader : styles.summaryHeader}>
                            {isNote ? (
                              <>
                                <div className={styles.noteAuthor}>
                                  <span className={styles.noteIcon}>📝</span>
                                  <span className={styles.noteCoachName}>{summary.objective_title || 'Coach Note'}</span>
                                </div>
                                <div className={styles.noteActions}>
                                  <button onClick={() => handleEditSummary(summary)} className={styles.editButton}>Edit</button>
                                  <button onClick={() => handleDeleteSummary(summary.id)} className={styles.deleteButton}>Delete</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <h4 className={styles.summaryTitle}>
                                  Week {summary.week_number}
                                  {summary.objective_title && ` - ${summary.objective_title}`}
                                </h4>
                                <span className={styles.summaryDate}>
                                  {new Date(summary.generated_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </>
                            )}
                          </div>

                          {isNote ? (
                            <>
                              <MarkdownContent
                                content={summary.summary_text}
                                className={styles.noteText}
                              />
                              <div className={styles.noteFooter}>
                                <span className={styles.noteDate}>
                                  {new Date(summary.createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </span>
                                {summary.updatedAt !== summary.createdAt && (
                                  <span className={styles.noteUpdated}>(edited)</span>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              {summary.summary_text && (
                                <div className={styles.summarySection}>
                                  <h5>Summary</h5>
                                  <MarkdownContent
                                    content={summary.summary_text}
                                    className={styles.summaryText}
                                  />
                                </div>
                              )}

                              {summary.behavioral_patterns && (
                                <div className={styles.summarySection}>
                                  <h5>Behavioral Patterns</h5>
                                  <MarkdownContent
                                    content={summary.behavioral_patterns}
                                    className={styles.summaryText}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>No summaries or notes yet. Add a note or wait for automated weekly summaries.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className={styles.content}>
          {history?.sessions && history.sessions.length > 0 ? (
            <div className={styles.sessionsList}>
              {history.sessions.map((session) => (
                <div key={session.id} className={styles.session}>
                  <div className={styles.sessionHeader}>
                    <span className={styles.sessionDate}>
                      {new Date(session.scheduled_start).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className={`${styles.sessionStatus} ${styles[`status-${session.status}`]}`}>
                      {session.status}
                    </span>
                  </div>

                  {session.notes && session.notes.length > 0 && (
                    <div className={styles.sessionNotes}>
                      <h5>Session Notes</h5>
                      {session.notes.map((note) => (
                        <div key={note.id} className={styles.note}>
                          <p>{note.note_text}</p>
                          <div className={styles.noteFooter}>
                            <span className={styles.noteAuthor}>{note.coach?.display_name || 'Coach'}</span>
                            <span className={styles.noteDate}>
                              {new Date(note.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No coaching sessions yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserDetailView;
